import { onList } from '~/calendar/views'

import { Bookmark } from './Bookmark'

import s from './Marks.module.css'
import { Star } from './Star'

/**
 * The three things you can do to a gig from its card: save it, say you are going, follow
 * the act.
 *
 * One component, because the calendar had its own pair written inline and the front page
 * had none at all — so a gig on one half of the site could be acted on and the same gig on
 * the other half could only be looked at. Today has been a long lesson in what two
 * descriptions of one thing cost, so this is the description.
 *
 * **Nothing at all when nobody is signed in.** Not disabled, not a control that leads to a
 * login: a heart offered to somebody who cannot have one is a promise the page then breaks.
 */
export interface Gig {
  id: string
  liked?: boolean
  going?: boolean
  picked?: boolean
  starred?: boolean
  act?: { id: number; name: string } | null
  /**
   * Whether the night has already happened, so the tick can be in the right tense.
   *
   * The sheet settled this vocabulary — `Decide` says "I was there" over "I am going" — and
   * the marks were left saying "You're going" about a gig last Tuesday. Recorded as fixed
   * once already, on the grounds that "the card and row marks are icons with no tense",
   * which was true of the icons and not of the tooltips they carry.
   */
  past?: boolean
}

export interface MarksProps {
  gig: Gig
  me: { isEditor: boolean } | null
  onSave(id: string, on: boolean): void
  onGoing(id: string, on: boolean): void
  /** Absent where following is not offered — the star is the newest of the three and not
   *  every surface wants it. */
  onStar?(artistId: number, on: boolean): void
}

function Tick({ fill }: { fill: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <path
        d="m4.5 12.5 5 5 10-11"
        fill="none"
        stroke="currentColor"
        strokeWidth={fill ? 3 : 1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function Marks({ gig, me, onSave, onGoing, onStar }: MarksProps) {
  if (!me) return null
  const hearted = onList(gig as { picked: boolean; liked: boolean }, me)
  const act = gig.act

  return (
    <>
      <button
        type="button"
        className={s.mark}
        data-mark=""
        aria-pressed={hearted}
        title={hearted ? 'Saved' : 'Save this'}
        onClick={ev => {
          // The card is a link. Without this, hearting a gig opens it.
          ev.stopPropagation()
          ev.preventDefault()
          onSave(gig.id, !hearted)
        }}
      >
        <Bookmark fill={hearted} strokeWidth={1.7} />
      </button>

      <button
        type="button"
        className={`${s.mark} ${s.going}`}
        data-mark=""
        aria-pressed={Boolean(gig.going)}
        title={
          gig.past
            ? gig.going
              ? 'You were there'
              : 'Mark that you were there'
            : gig.going
              ? "You're going"
              : 'Mark as going'
        }
        onClick={ev => {
          ev.stopPropagation()
          ev.preventDefault()
          onGoing(gig.id, !gig.going)
        }}
      >
        <Tick fill={Boolean(gig.going)} />
      </button>

      {onStar && act ? (
        <button
          type="button"
          className={`${s.mark} ${s.star}`}
          data-mark=""
          aria-pressed={Boolean(gig.starred)}
          title={gig.starred ? `Following ${act.name}` : `Follow ${act.name}`}
          onClick={ev => {
            ev.stopPropagation()
            ev.preventDefault()
            onStar(act.id, !gig.starred)
          }}
        >
          <Star fill={Boolean(gig.starred)} strokeWidth={1.6} />
        </button>
      ) : null}
    </>
  )
}
